// Copyright (c) 2019 Celadon Development LLC, All rights reserved.
// Author Alexander Mikholap <alexander.mikholap@celadon.ae>

import {
  put, call, takeEvery, select
} from 'redux-saga/effects';

import {
  validateMobileService,
  validateEmailService,
  confirmOrderForBookshopService,
  completingConfirmForBookshop,
  validateDiscountCodeService,
} from './service';
import { basketService } from '../../basket/store/service';

import * as types from './actionTypes';
import * as getPaidActions from './actions';
import { getCustomerFormStatus, getDiscountCode, getActiveDiscountCode } from './selectors';

import * as basketActions from '../../basket/store/actions';
import * as confirmOrderActions from '../../choose-delivery/store/actions';
import history from '../../../services/history';

import { getTotal } from '../../basket/store/selectors';
import { getPrice } from '../../../services/priceHelper';


function* validateHandler(res, status, error) {
  if (res.ok) {
    if (res.body.result) {
      if (status.Success || status.Success === null) {
        yield put(getPaidActions.validateFormSuccess());
      } else {
        yield put(getPaidActions.validateFormFailed(status));
      }
    } else {
      yield put(getPaidActions.validateFormFailed({
        Message: error,
      }));
    }
  }
}


function* validateMobileSaga({ payload }) {
  const error = payload.phone_number
    ? 'Invalid phone number.'
    : 'Please enter customer phone number.';

  const res = yield call(validateMobileService, payload);
  const status = yield select(getCustomerFormStatus);

  yield call(validateHandler, res, status, error);
}


function* validateEmailSaga({ payload }) {
  const error = payload.email
    ? 'Invalid email.'
    : 'Please enter customer email.';

  const res = yield call(validateEmailService, payload);
  const status = yield select(getCustomerFormStatus);

  yield call(validateHandler, res, status, error);
}


function* confirmOrderForBookshopSaga({ payload }) {
  const {
    FullName,
    Email,
    Mobile,
    CustomerID,
    CartID,
    Token,
  } = payload;

  const confirmData = {
    Token,
    CustomerID,
    CartID
  };

  const basket = yield call(basketService, { CartID });
  const result = yield call(confirmOrderForBookshopService, confirmData);

  if (result.ok) {
    const orderId = JSON.parse(result.body).OrderID;
    const data = {
      FullName,
      Email,
      Mobile,
      Token,
      BookshopID: CustomerID,
      OrderID: orderId
    };

    const completingResult = yield call(completingConfirmForBookshop, data);


    if (completingResult.ok) {
      const total = yield select(getTotal);
      const activeDiscountCode = yield select(getActiveDiscountCode);

      const totalCost = basket.body.CartDetails.reduce((acc, item) => acc + item.SellbackPrice, 0);
      const items = basket.body.CartDetails.map((item) => {
        return {
          item_id: item.EAN,
          item_name: item.Title,
          currency: 'GBP',
          price: item.SellbackPrice,
          quantity: 1
        };
      });

      window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      window.dataLayer.push({
        event: 'purchase',
        customer_email: `${data.Email}` ?? '',
        customer_phone: `${data.Mobile}` ?? '', // make sure to add the country code
        user_id: `${data.BookshopID}` ?? '',
        ecommerce: {
          transaction_id: `${data.OrderID}`, // Order id
          value: totalCost,
          currency: 'GBP',
          coupon: activeDiscountCode?.Code ?? '',
          items,
        }
      });

      localStorage.removeItem('CartID');
      yield put(basketActions.basketReset());
      yield put(getPaidActions.resetActiveDiscountCode());
      localStorage.setItem('wasQualified', 0);
      history.push(
        '/order-confirmed',
        {
          total: getPrice(activeDiscountCode, total),
          orderId,
          bookshopCustomerId: completingResult.body,
        }
      );
    }

  } else {
    const message = result.body.Reason || 'Some error';
    yield put(confirmOrderActions.requestConfirmFailed({
      message,
      status: false,
    }));
  }

}

function* validateFormSaga({ payload }) {
  const {
    key,
    email,
    country,
    phone_number,
    full_name,
    get_network,
    CustomerID,
    Token,
    CartID,
  } = payload;

  if (full_name.trim() === '') {
    yield put(getPaidActions.validateFormFailed({
      Message: 'Please enter customer name.',
    }));
  } else {
    yield put(getPaidActions.validateFormSuccess());
  }

  yield call(validateEmailSaga, { payload: { key, email } });
  yield call(validateMobileSaga, {
    payload: {
      key,
      phone_number,
      country,
      get_network
    }
  });

  const status = yield select(getCustomerFormStatus);
  if (status.Success) {
    yield call(confirmOrderForBookshopSaga, {
      payload: {
        FullName: full_name,
        Email: email,
        Mobile: phone_number,
        CartID,
        CustomerID,
        Token,
      }
    });
  }
}

function* validateDiscountCodeSaga() {
  const activeDiscountCode = yield select(getActiveDiscountCode);

  if (!Object.keys(activeDiscountCode).length) {

    const discountCode = yield select(getDiscountCode);
    const tempOrderId = localStorage.getItem('CartID');
    const id = localStorage.getItem('loggedID');
    const token = localStorage.getItem('token');

    const data = {
      CustomerId: id,
      Token: token,
      DiscountCode: discountCode,
      TempOrderId: tempOrderId,
    };

    const res = yield call(validateDiscountCodeService, data);
    if (res.ok) {

      yield put(getPaidActions.setActiveDiscountCode(res.body));
      yield put(getPaidActions.validateDiscountCodeStatus({
        Message: `Discount code ${res.body.Code} has been applied`,
        Status: res.ok,
      }));
      yield put(getPaidActions.resetDiscountCode());

    } else {
      yield put(getPaidActions.validateDiscountCodeStatus({
        Message: `Discount code ${discountCode} was not found`,
        Status: res.ok,
      }));
    }
  } else {
    yield put(getPaidActions.validateDiscountCodeStatus({
      Message: 'You have already activated discount code.',
      Status: false,
    }));
  }
}

const getPaidSagas = [
  takeEvery(types.GETPAID_CONFIRM_ORDER_FOR_BOOKSHOP, confirmOrderForBookshopSaga),
  takeEvery(types.GETPAID_VALIDATE_MOBILE, validateMobileSaga),
  takeEvery(types.GETPAID_VALIDATE_EMAIL, validateEmailSaga),
  takeEvery(types.GETPAID_VALIDATE_FORM, validateFormSaga),
  takeEvery(types.VALIDATE_DISCOUNT_CODE, validateDiscountCodeSaga),
];

export default getPaidSagas;
